import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, AlertTriangle, CheckCircle, XCircle } from 'lucide-react';
import { usePlanLimitsTranslations } from '@/hooks/usePlanLimitsTranslations';
import api from '@/services/api';
import { cn } from '@/lib/utils';

interface UsageStatus {
  resource: string;
  current: number;
  limit: number | null;
  percentage: number;
  status: 'healthy' | 'caution' | 'warning' | 'critical' | 'exceeded';
  warningLevel?: string;
  estimatedDaysUntilLimit?: number;
}

interface PlanUsageData {
  organizationName: string;
  currentPlan: string;
  usage: UsageStatus[];
  suggestedUpgrade?: {
    targetPlan: string;
    benefits: string[];
    reasons: string[];
  };
}

function getStatusColor(status: string) {
  switch (status) {
    case 'healthy':
      return 'text-green-600 bg-green-50 border-green-200';
    case 'caution':
      return 'text-yellow-600 bg-yellow-50 border-yellow-200';
    case 'warning':
      return 'text-orange-600 bg-orange-50 border-orange-200';
    case 'critical':
      return 'text-red-600 bg-red-50 border-red-200';
    case 'exceeded':
      return 'text-red-700 bg-red-100 border-red-300';
    default:
      return 'text-gray-600 bg-gray-50 border-gray-200';
  }
}

function getStatusIcon(status: string) {
  switch (status) {
    case 'healthy':
      return <CheckCircle className="size-4 text-green-600" />;
    case 'caution':
    case 'warning':
      return <AlertTriangle className="size-4 text-yellow-600" />;
    case 'critical':
    case 'exceeded':
      return <XCircle className="size-4 text-red-600" />;
    default:
      return <CheckCircle className="size-4 text-gray-600" />;
  }
}

function getProgressColor(percentage: number) {
  if (percentage >= 95) return 'bg-red-500';
  if (percentage >= 85) return 'bg-orange-500';
  if (percentage >= 75) return 'bg-yellow-500';
  return 'bg-green-500';
}

export function PlanLimitsDashboard() {
  const i18n = usePlanLimitsTranslations();

  const {
    data: usageData,
    isLoading,
    error,
  } = useQuery<PlanUsageData>({
    queryKey: ['plan-usage-status'],
    queryFn: async () => {
      const response = await api.get('/api/plan-limits/api/usage-status/');
      return response.data;
    },
    refetchInterval: 5 * 60 * 1000, // Refetch every 5 minutes
  });

  if (isLoading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-8">
          <div className="flex items-center space-x-2">
            <Loader2 className="size-6 animate-spin text-primary" />
            <span className="text-muted-foreground">{i18n.loading}</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardContent className="py-8">
          <Alert variant="destructive">
            <AlertTriangle className="size-4" />
            <AlertDescription>{i18n.error}</AlertDescription>
          </Alert>
        </CardContent>
      </Card>
    );
  }

  if (!usageData) {
    return null;
  }

  const hasWarnings = usageData.usage.some(u =>
    ['warning', 'critical', 'exceeded'].includes(u.status)
  );
  const hasExceeded = usageData.usage.some(u => u.status === 'exceeded');

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold">{i18n.title}</h3>
          <p className="text-sm text-muted-foreground">{i18n.subtitle}</p>
        </div>
        <Badge variant="secondary" className="text-sm font-medium">
          {i18n.planDetails[usageData.currentPlan as keyof typeof i18n.planDetails] ||
            usageData.currentPlan}
        </Badge>
      </div>

      {/* Warnings Alert */}
      {hasWarnings && (
        <Alert
          className={cn(
            hasExceeded ? 'border-red-200 bg-red-50' : 'border-yellow-200 bg-yellow-50'
          )}
        >
          <AlertTriangle
            className={cn('size-4', hasExceeded ? 'text-red-600' : 'text-yellow-600')}
          />
          <AlertDescription className={cn(hasExceeded ? 'text-red-800' : 'text-yellow-800')}>
            {hasExceeded ? i18n.warnings.exceeded('limits') : i18n.warnings.approaching}{' '}
            {i18n.warnings.upgradeRecommended}
          </AlertDescription>
        </Alert>
      )}

      {/* Usage Cards Grid */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        {usageData.usage.map(usage => {
          const isUnlimited = usage.limit === null;
          const progressValue = isUnlimited ? 0 : Math.min(usage.percentage, 100);

          return (
            <Card key={usage.resource} className="relative overflow-hidden">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-sm font-medium">
                    {i18n.resources[usage.resource as keyof typeof i18n.resources]?.title ||
                      usage.resource}
                  </CardTitle>
                  {getStatusIcon(usage.status)}
                </div>
                <CardDescription className="text-xs">
                  {i18n.resources[usage.resource as keyof typeof i18n.resources]?.description}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                {/* Usage Numbers */}
                <div className="space-y-1">
                  <div className="flex items-baseline justify-between">
                    <span className="text-2xl font-bold">
                      {usage.resource === 'storage'
                        ? `${(usage.current / 1024).toFixed(1)} ${i18n.resources.storage.unit}`
                        : usage.current.toLocaleString()}
                    </span>
                    <span className="text-sm text-muted-foreground">
                      {isUnlimited
                        ? i18n.usage.unlimited
                        : usage.resource === 'storage'
                          ? `${i18n.usage.of} ${(usage.limit! / 1024).toFixed(0)} ${i18n.resources.storage.unit}`
                          : `${i18n.usage.of} ${usage.limit!.toLocaleString()}`}
                    </span>
                  </div>

                  {!isUnlimited && (
                    <div className="space-y-1">
                      <Progress
                        value={progressValue}
                        className="h-2"
                        style={
                          {
                            '--progress-foreground': getProgressColor(usage.percentage),
                          } as React.CSSProperties
                        }
                      />
                      <div className="flex items-center justify-between text-xs text-muted-foreground">
                        <span>{usage.percentage.toFixed(1)}% used</span>
                        {usage.status !== 'exceeded' && (
                          <span>{i18n.usage.remaining(usage.limit! - usage.current)}</span>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                {/* Status Badge */}
                <Badge variant="outline" className={cn('text-xs', getStatusColor(usage.status))}>
                  {i18n.status[usage.status as keyof typeof i18n.status]}
                </Badge>

                {/* Estimated Days */}
                {usage.estimatedDaysUntilLimit && usage.estimatedDaysUntilLimit > 0 && (
                  <p className="text-xs text-muted-foreground">
                    {i18n.usage.daysUntilLimit(usage.estimatedDaysUntilLimit)}
                  </p>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
